import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  Loader2, AlertCircle, CheckCircle2, ShieldCheck,
  CalendarDays, MapPin, Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import PaymentMethodStep from "../components/PaymentMethodStep";

/**
 * PayInvoiceByToken — iter230 (2026-03-02)
 *
 * Public, no-login-required payment page mounted at `/pay/:token`.
 *
 * iter230 owner directive:
 *   "The customer must see the complete JDSN payment step exactly like
 *    the last step of a new booking: progress steps (Estimate → Details
 *    → Payment → Confirm), Leave a tip ($5/$10/$20/Custom), 'How would
 *    you like to pay?', 'Pay $X your way', Credit / Debit Card,
 *    Payment authorization, Pay $X with Card button, and Order Summary
 *    — but with the existing booking, service, date, amount, and
 *    customer already loaded."
 *
 * This page reuses the exact `PaymentMethodStep` component that Book.jsx
 * renders on step 3, so the UI is pixel-identical to the booking flow.
 * `onConfirm` here posts to `/api/invoice/token/{token}/pay` with the
 * tip amount — the backend enforces the atomic duplicate-payment guard
 * (iter229) and creates the Stripe Checkout Session.
 *
 * Security model: the URL token is the credential — cryptographically
 * strong 24-byte urlsafe secret. It scopes the two public endpoints
 * (`/api/invoice/token/<t>` and `/api/invoice/token/<t>/pay`) to a
 * single booking. No customer login required, no session cookie sent.
 */

// Use a bare axios instance (no credentials, no shared interceptors) so
// this public page never accidentally sends a session cookie.
const pubApi = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
  withCredentials: false,
});

export default function PayInvoiceByToken() {
  const { token } = useParams();
  const [params, setParams] = useSearchParams();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const paidFlash = params.get("paid") === "1";
  const paidSessionId = params.get("session_id") || "";
  const cancelledFlash = params.get("cancelled") === "1";

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await pubApi.get(`/invoice/token/${token}`);
        if (!cancelled) setInvoice(data);
      } catch (e) {
        if (cancelled) return;
        const detail = e?.response?.data?.detail;
        setError({
          code: e?.response?.status,
          message:
            typeof detail === "string"
              ? detail
              : "This invoice link is no longer valid.",
        });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  // iter231 — Return-flow confirmation. If the customer just came back
  // from Stripe (`?paid=1&session_id=cs_...`), call the backend confirm
  // endpoint that actively queries Stripe for the session's paid status
  // and promotes the booking IMMEDIATELY — no waiting for the webhook.
  // If confirm still reports unpaid (extremely rare), poll GET
  // `/invoice/token/<token>` every 1.5s for up to 20s as a safety net.
  useEffect(() => {
    if (!paidFlash) return;
    let cancelled = false;
    (async () => {
      // 1) Active confirm via Stripe.
      try {
        const { data } = await pubApi.post(
          `/invoice/token/${token}/confirm`,
          { session_id: paidSessionId || null },
        );
        if (cancelled) return;
        if (data?.invoice) setInvoice(data.invoice);
        if (data?.paid) return;
      } catch { /* fall through to polling */ }
      // 2) Polling safety net — up to 20s at 1.5s cadence.
      for (let i = 0; i < 14 && !cancelled; i++) {
        await new Promise((r) => setTimeout(r, 1500));
        try {
          const { data } = await pubApi.get(`/invoice/token/${token}`);
          if (cancelled) return;
          setInvoice(data);
          if (data?.paid) return;
        } catch { /* ignore */ }
      }
    })();
    const next = new URLSearchParams(params);
    next.delete("paid");
    next.delete("session_id");
    setParams(next, { replace: true });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paidFlash]);

  useEffect(() => {
    if (cancelledFlash) {
      toast.info("Payment cancelled. You can pay this invoice anytime.");
      const next = new URLSearchParams(params);
      next.delete("cancelled");
      setParams(next, { replace: true });
    }
  }, [cancelledFlash]);

  const handlePayConfirm = async (_methodId, { tipAmount } = {}) => {
    if (submitting) return; // extra guard against double-click race
    setSubmitting(true);
    try {
      const { data } = await pubApi.post(`/invoice/token/${token}/pay`, {
        origin_url: window.location.origin,
        tip_amount: tipAmount || 0,
      });
      if (!data?.url) throw new Error("no checkout url");
      window.location.assign(data.url);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      const detailCode = (detail && typeof detail === "object" && detail.code) || null;
      const detailMsg  = (detail && typeof detail === "object" && detail.message) || null;
      // iter229 — already paid; re-fetch invoice so the UI flips.
      if (status === 409 || detailCode === "already_paid") {
        try {
          const { data: fresh } = await pubApi.get(`/invoice/token/${token}`);
          setInvoice(fresh);
        } catch { /* ignore */ }
        toast.info("This invoice has already been paid.");
        setSubmitting(false);
        return;
      }
      // iter229 — concurrent Pay Now in flight.
      if (status === 429 || detailCode === "payment_in_progress") {
        toast.info(detailMsg || "Another payment is being processed. Please wait a moment.");
        setSubmitting(false);
        return;
      }
      toast.error(
        typeof detail === "string"
          ? detail
          : detailMsg || "Couldn't open secure checkout. Please try again.",
      );
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Shell testid="pay-invoice-loading">
        <div className="flex items-center gap-3 justify-center py-16 text-gray-600">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span className="text-sm font-medium">Loading your invoice…</span>
        </div>
      </Shell>
    );
  }

  if (error) {
    return (
      <Shell testid="pay-invoice-error">
        <div className="max-w-lg mx-auto mt-6">
          <div className="flex items-start gap-3 bg-white border border-rose-200 rounded-3xl p-6">
            <div className="w-10 h-10 rounded-full bg-rose-100 text-rose-700 flex items-center justify-center flex-shrink-0">
              <AlertCircle className="w-5 h-5" />
            </div>
            <div>
              <div className="text-lg font-extrabold text-gray-900 font-[Outfit] tracking-tight">
                Link not found
              </div>
              <div className="text-sm text-gray-600 mt-1">{error.message}</div>
              <div className="text-xs text-gray-500 mt-3">
                Ask JDSN Lawn Care to resend your payment link.
              </div>
              <a
                href="sms:+19048328730?body=Hi%20JDSN%2C%20my%20payment%20link%20isn%27t%20working%20-%20please%20resend."
                className="mt-3 inline-flex items-center h-10 px-4 rounded-full bg-emerald-700 text-white font-extrabold text-sm hover:bg-emerald-800"
                data-testid="pay-invoice-text-us"
              >
                Text us
              </a>
            </div>
          </div>
        </div>
      </Shell>
    );
  }

  if (invoice?.paid) {
    return (
      <Shell testid="pay-invoice-paid">
        <div className="max-w-lg mx-auto mt-6">
          <div className="bg-white rounded-3xl border border-emerald-200 shadow-sm p-6 sm:p-8 text-center">
            <div className="mx-auto w-14 h-14 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center">
              <CheckCircle2 className="w-8 h-8" />
            </div>
            <div className="mt-3 text-3xl font-extrabold text-gray-900 font-[Outfit] tracking-tight"
                  data-testid="pay-invoice-paid-title">
              Already Paid ✓
            </div>
            <div className="text-sm text-gray-600 mt-1">
              Thank you! Your <b>{invoice.service_name}</b> invoice for
              {" "}<b>${(invoice.amount_due || 0).toFixed(2)}</b> is settled.
            </div>
            {invoice.paid_at && (
              <div className="text-xs text-gray-500 mt-2">
                Payment received {new Date(invoice.paid_at).toLocaleString()}
              </div>
            )}
            <div className="mt-6 text-[11px] text-gray-500">
              Booking #{(invoice.booking_id || "").slice(-6)}
            </div>
          </div>
        </div>
      </Shell>
    );
  }

  // Normal render — the exact Book.jsx step 3 UI, pre-loaded with this
  // booking's data.
  const amount = Number(invoice?.amount_due || 0);
  const firstName = invoice?.customer_first_name || "there";

  return (
    <Shell testid="pay-invoice-card">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8" data-testid="pay-page">
        <div className="text-xs uppercase tracking-[0.2em] text-[#047857] font-bold">
          Book a Service
        </div>
        <h1 className="mt-1 text-4xl sm:text-5xl font-extrabold tracking-tighter text-gray-900 font-[Outfit]">
          How would you like to pay?
        </h1>
        {/* Progress steps — matches Book.jsx step 3 */}
        <div className="mt-6 flex flex-wrap items-center gap-x-3 gap-y-2 text-sm"
              data-testid="pay-progress-steps">
          {["Estimate", "Details", "Payment", "Confirm"].map((label, i) => {
            const n = i + 1;
            const active = n === 3;
            const done = n < 3;
            return (
              <div key={label} className="flex items-center gap-2">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${active ? "bg-[#047857] text-white" : done ? "bg-[#10B981] text-white" : "bg-gray-200 text-gray-600"}`}>
                  {done ? <CheckCircle2 className="w-4 h-4" /> : n}
                </div>
                <span className={`font-semibold ${active ? "text-gray-900" : "text-gray-500"}`}>
                  {label}
                </span>
                {i < 3 && <div className="w-6 sm:w-8 h-px bg-gray-300 mx-1" />}
              </div>
            );
          })}
        </div>

        {/* Greeting + booking recap */}
        <div className="mt-6 rounded-3xl border border-emerald-100 bg-emerald-50/60 p-5 sm:p-6"
              data-testid="pay-invoice-recap">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="inline-flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-[0.14em] rounded-full border bg-orange-50 text-orange-800 border-orange-200 px-2 py-0.5"
                   data-testid="payment-due-badge">
              <span className="w-1.5 h-1.5 rounded-full bg-orange-500" aria-hidden />
              Payment Due
            </span>
            <span className="text-[11px] font-mono text-gray-500">
              Booking #{(invoice?.booking_id || "").slice(-6)}
            </span>
          </div>
          <p className="mt-3 text-sm text-gray-700">
            Hi <b>{firstName}</b>, here&apos;s your JDSN Lawn Care service.
            Everything&apos;s ready — pick a tip (optional) and pay to
            confirm.
          </p>
          <div className="mt-4 grid sm:grid-cols-3 gap-3 text-sm">
            <div className="flex items-start gap-2 text-gray-800">
              <Sparkles className="w-4 h-4 mt-0.5 text-emerald-700 flex-shrink-0" />
              <div>
                <div className="text-[10px] uppercase tracking-[0.16em] text-emerald-800 font-extrabold">
                  Service
                </div>
                <div className="font-extrabold" data-testid="pay-invoice-service">
                  {invoice?.service_name || "JDSN Service"}
                </div>
              </div>
            </div>
            {invoice?.service_date && (
              <div className="flex items-start gap-2 text-gray-800">
                <CalendarDays className="w-4 h-4 mt-0.5 text-emerald-700 flex-shrink-0" />
                <div>
                  <div className="text-[10px] uppercase tracking-[0.16em] text-emerald-800 font-extrabold">
                    Date
                  </div>
                  <div className="font-extrabold" data-testid="pay-invoice-date">
                    {invoice.service_date}
                    {invoice.service_time && (<> · {invoice.service_time}</>)}
                  </div>
                </div>
              </div>
            )}
            {invoice?.formatted_address && (
              <div className="flex items-start gap-2 text-gray-800 min-w-0">
                <MapPin className="w-4 h-4 mt-0.5 text-emerald-700 flex-shrink-0" />
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.16em] text-emerald-800 font-extrabold">
                    Address
                  </div>
                  <div className="font-extrabold truncate"
                        data-testid="pay-invoice-address">
                    {invoice.formatted_address}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* iter230 — Reuse the EXACT booking Payment step so this page
            is pixel-identical to Book.jsx step 3. */}
        <div className="mt-8">
          <PaymentMethodStep
            amount={amount}
            bookingNote={`${invoice?.service_name || "JDSN Service"}${invoice?.service_date ? ` · ${invoice.service_date}` : ""}`}
            submitting={submitting}
            onBack={() => window.location.assign("https://www.jdsnlawncare.com")}
            onConfirm={handlePayConfirm}
            wallet={null}
          />
        </div>
      </div>
    </Shell>
  );
}

function Shell({ children, testid }) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-emerald-50/60 py-6"
          data-testid={testid || "pay-invoice-shell"}>
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        <div className="text-center pb-4">
          <div className="inline-flex items-center gap-2 text-[11px] font-extrabold uppercase tracking-[0.2em] text-emerald-800">
            <ShieldCheck className="w-3 h-3" />
            JDSN Lawn Care · Secure Payment
          </div>
        </div>
      </div>
      {children}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 mt-8 text-center text-[11px] text-gray-500">
        Questions? Text us at{" "}
        <a href="sms:+19048328730" className="font-bold text-emerald-800">
          904-832-8730
        </a>
      </div>
    </div>
  );
}
